// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { htmlEntities } from '@automator/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-sentence' )
export class AutomatorLogSentence extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: String, attribute: false } ) accessor sentence = '';

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`${ this.cleanSentence }`;
	}

	/**
	 * This converts the old HTML template of the sentences
	 *
	 * @return {TemplateResult} The sentence
	 */
	get cleanSentence() {
		// Check if it's the old format
		if ( this.sentenceVersion === 'item-old' ) {
			return this.cleanSentenceItemOld;
		}

		if ( this.sentenceVersion === 'condition' ) {
			return this.cleanSentenceCondition;
		}

		if ( this.sentenceVersion === 'condition-old' ) {
			return this.cleanSentenceConditionOld;
		}

		if ( this.sentenceVersion === 'standard' ) {
			return this.cleanSentenceStandard;
		}

		// We're decoding the entities, but Lit will encoding them again. We're returning a string, not a TemplateResult
		return htmlEntities.decode( this.sentence );
	}

	/**
	 * *Backwards compatibility*
	 * Cleans the sentence using the template of sentences coming from the first version
	 *
	 * <div class="item-title__describe-container">
	 *		<div class="item-title__describe" dir="auto">
	 *			<span class="item-title__normal">Enroll the user in </span>
	 *			<span class="item-title__token item-title__token--filled" data-token-id="LDCOURSE" data-options-id="LDCOURSE" uap-tooltip="Introduction to Music Production">
	 *				<span class="item-title__token-label">Course:</span> Introduction to ... usic Production
	 *			</span>
	 *		</div>
	 * </div>
	 *
	 * to
	 *
	 * <div class="sentence" dir="auto">
	 * 		<span class="sentence-plain">Enroll the user in </span>
	 * 		<uo-tooltip>
	 * 			Introduction to Music Production
	 * 			<span slot="target">
	 * 				<span class="sentence-pill sentence-pill--filled">
	 * 					<span class="sentence-pill-label">Course:</span> Introduction to ... usic Production
	 * 				</span>
	 * 			</span>
	 * 		</uo-tooltip>
	 * </div>
	 *
	 * @return {Node} the sentence
	 */
	get cleanSentenceItemOld() {
		// Parse the HTML in the sentence
		const $wrapper = document.createElement( 'div' );
		$wrapper.innerHTML = htmlEntities.decode( this.sentence );

		let $sentence = $wrapper.querySelector( '.item-title__describe' ) ?
			$wrapper.querySelector( '.item-title__describe' ) :
			$wrapper.firstChild;

		// Change main class
		$sentence.classList.remove( 'item-title__describe' );
		$sentence.classList.add( 'sentence', 'sentence--old' );

		// Replace classes of normal text nodes
		$sentence.querySelectorAll( '.item-title__normal' ).forEach( $textElement => {
			// Replace classes
			$textElement.classList.remove( 'item-title__normal' );
			$textElement.classList.add( 'sentence-plain' );
		} );

		// Replace the class of pills
		$sentence.querySelectorAll( '.item-title__token' ).forEach( $pill => {
			// Replace classes
			$pill.classList.remove( 'item-title__token' );
			$pill.classList.add( 'sentence-pill' );

			if ( $pill.classList.contains( 'item-title__token--filled' ) ) {
				$pill.classList.remove( 'item-title__token--filled' )
				$pill.classList.add( 'sentence-pill--filled' )
			}

			// Change the class of the label
			if ( $pill.querySelector( '.item-title__token-label' ) ) {
				const $pillLabel = $pill.querySelector( '.item-title__token-label' );
				$pillLabel.classList.remove( 'item-title__token-label' );
				$pillLabel.classList.add( 'sentence-pill-label' );
			}

			// Remove unnecessary attributes
			$pill.removeAttribute( 'data-token-id' );
			$pill.removeAttribute( 'data-options-id' );

			// Check if we have to add a tooltip
			// if ( $pill.hasAttribute( 'uap-tooltip' ) ) {
			// 	// Create tooltip
			// 	const $tooltip = document.createElement( 'uo-tooltip' );
			// 	$tooltip.innerText = $pill.getAttribute( 'uap-tooltip' );
			// 	$pill.removeAttribute( 'uap-tooltip' );

			// 	// Place tooltip before pill
			// 	$pill.insertAdjacentElement( 'beforeBegin', $tooltip );

			// 	// Create target
			// 	const $tooltipTarget = document.createElement( 'span' );
			// 	$tooltipTarget.setAttribute( 'slot', 'target' );
			// 	$tooltipTarget.append( $pill );
			// 	$tooltip.append( $tooltipTarget );
			// }
		} );

		return $sentence;
	}

	/**
	 * *Backwards compatibility*
	 * Cleans the sentence using the template of sentences coming from the conditions
	 *
	 * <span class="uap-dynamic-sentence" dir="auto">
	 *		<span class="uap-dynamic-sentence-plain">The user has completed </span>
	 *		<span class="uap-dynamic-sentence-pill uap-dynamic-sentence-pill--filled" data-field="LESSON">
	 *			<span class="uap-dynamic-sentence-pill-label">Lesson: </span>
	 *			<span class="uap-text-with-tokens">Getting Started</span>
	 *		</span>
	 * </span>
	 *
	 * to
	 *
	 * <span class="sentence" dir="auto">
	 * 		<span class="sentence-plain">The user has completed </span>
	 * 		<span class="sentence-pill sentence-pill--filled">
	 * 			<span class="sentence-pill-label">Lesson: </span>
	 * 			<span>Getting Started</span>
	 * 		</span>
	 * </span>
	 *
	 * @return {Node} the sentence
	 */
	get cleanSentenceCondition() {
		// Parse the HTML in the sentence
		const $wrapper = document.createElement( 'div' );
		$wrapper.innerHTML = htmlEntities.decode( this.sentence );
		const $sentence = $wrapper.querySelector( '.uap-dynamic-sentence' );

		// Change main class
		$sentence.classList.remove( 'uap-dynamic-sentence' );
		$sentence.classList.add( 'sentence', 'sentence--condition' );

		// Replace classes of normal text nodes
		$sentence.querySelectorAll( '.uap-dynamic-sentence-plain' ).forEach( $textElement => {
			// Replace classes
			$textElement.classList.remove( 'uap-dynamic-sentence-plain' );
			$textElement.classList.add( 'sentence-plain' );
		} );

		// Replace classes of the pills
		$sentence.querySelectorAll( '.uap-dynamic-sentence-pill' ).forEach( $pill => {
			// Replace classes
			$pill.classList.remove( 'uap-dynamic-sentence-pill' );
			$pill.classList.add( 'sentence-pill' );

			// Remove unnecessary attributes
			$pill.removeAttribute( 'data-field' );

			if ( $pill.classList.contains( 'uap-dynamic-sentence-pill--filled' ) ) {
				$pill.classList.remove( 'uap-dynamic-sentence-pill--filled' )
				$pill.classList.add( 'sentence-pill--filled' )
			}

			// Change the class of the label
			if ( $pill.querySelector( '.uap-dynamic-sentence-pill-label' ) ) {
				const $pillLabel = $pill.querySelector( '.uap-dynamic-sentence-pill-label' );
				$pillLabel.classList.remove( 'uap-dynamic-sentence-pill-label' );
				$pillLabel.classList.add( 'sentence-pill-label' );
			}

			// Remove unnecessary class
			if ( $pill.querySelector( '.uap-text-with-tokens' ) ) {
				$pill.querySelector( '.uap-text-with-tokens' ).classList.remove( 'uap-text-with-tokens' )
			}
		} );

		return $sentence;
	}

	/**
	 * *Backwards compatibility*
	 * Cleans the sentence using the template of sentences coming from old conditions
	 * This one is pretty straightforward
	 *
	 * 	<span class="sentence sentence--old-condition">
	 * 		<span class="sentence-plain">The user has completed </span>
	 * 		<span class="sentence-pill">
	 * 			<span class="sentence-pill-label">Course</span>: Build Web Apps with React & Firebase
	 * 		</span>
	 * 	</span>
	 *
	 * to
	 *
	 * 	<span class="sentence" dir="auto">
	 * 		<span class="sentence-plain">The user has completed </span>
	 * 		<span class="sentence-pill">
	 * 			<span class="sentence-pill-label">Course</span>: Build Web Apps with React & Firebase
	 * 		</span>
	 * 	</span>
	 *
	 * @return {Node} the sentence
	 */
	get cleanSentenceConditionOld() {
		// Parse the HTML in the sentence
		const $wrapper = document.createElement( 'div' );
		$wrapper.innerHTML = htmlEntities.decode( this.sentence );
		const $sentence = $wrapper.querySelector( '.sentence' );

		// Remove class `sentence--old-condition`
		$sentence.classList.remove( 'sentence--old-condition' );

		// Add dir="auto" attribute
		$sentence.setAttribute( 'dir', 'auto' );

		// Add wrapper around text node
		$sentence.childNodes.forEach( ( $sentencePart ) => {
			try {
				// Check if it's a text node
				if ( $sentencePart.nodeType !== Node.TEXT_NODE ) {
					return;
				}

				// Create wrapper
				const $plainWrapper = document.createElement( 'span' );
				$plainWrapper.classList.add( 'sentence-plain' );

				// Insert plainWrapper before the text node
				$sentence.insertBefore( $plainWrapper, $sentencePart );

				// Wrap the text node
				$plainWrapper.appendChild( $sentencePart );
			} catch ( e ) {}
		} );

		return $sentence;
	}

	/**
	 * The standard sentence
	 *
	 * @return {Node} the sentence
	 */
	get cleanSentenceStandard() {
		const $wrapper = document.createElement( 'div' );
		$wrapper.innerHTML = htmlEntities.decode( this.sentence );

		return $wrapper;
	}

	/**
	 * Returns the version of the sentence by finding some of the elements inside
	 *
	 * @return {Number} The version
	 */
	get sentenceVersion() {
		if (
			this.sentence.includes( 'item-title__token' ) ||
			this.sentence.includes( 'item&#45;title__token' )
		) {
			return 'item-old';
		}

		if (
			this.sentence.includes( 'uap-dynamic-sentence' ) ||
			this.sentence.includes( 'uap&#45;dynamic&#45;sentence' )
		) {
			return 'condition';
		}

		if (
			this.sentence.includes( 'sentence--old-condition' ) ||
			this.sentence.includes( 'sentence&#45;&#45;old&#45;condition' )
		) {
			return 'condition-old';
		}

		if (
			this.sentence.includes( 'sentence--standard' ) ||
			this.sentence.includes( 'sentence&#45;&#45;standard' )
		) {
			return 'standard';
		}
	}
}
